// VIPClientsManager - UPDATED 2025-01-28 - LABELS BLANCOS
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  FaCrown, FaPlus, FaEdit, FaTrash, FaSearch, FaCalendar, 
  FaSync, FaUserPlus, FaDollarSign, FaClock, FaExclamationTriangle,
  FaCheckCircle, FaTimesCircle, FaGift, FaPhone, FaEnvelope,
  FaMars, FaVenus, FaMoneyBillWave, FaUnlock, FaBell, FaKey, FaBan, FaInfoCircle
} from 'react-icons/fa';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SUBSCRIPTION_PERIODS = [
  { value: 'monthly', label: 'Mensual (1 mes)', months: 1 },
  { value: 'bimonthly', label: 'Bimestre (2 meses)', months: 2 },
  { value: 'quarterly', label: 'Trimestre (3 meses)', months: 3 },
  { value: 'semester', label: 'Semestre (6 meses)', months: 6 },
  { value: 'yearly', label: 'Anual (12 meses)', months: 12 }
];

// Planes por defecto (se sobreescriben con los de la API)
const DEFAULT_PLAN_TYPES = [
  { value: 'basic', label: '🌸 Fit Evolution', color: 'bg-gray-500' },
  { value: 'premium', label: '🥈 Premium', color: 'bg-purple-500' },
  { value: 'gold', label: '🥇 Gold', color: 'bg-yellow-500' },
  { value: 'competitivo', label: '🏆 Competitivo', color: 'bg-orange-500' }
];

export default function VIPClientsManager() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // Filtro por tipo de programa (Fit Evolution vs Asesoría 1 a 1)
  const [filterProgram, setFilterProgram] = useState('all');
  const [activeTab, setActiveTab] = useState('clients');
  
  // Planes dinámicos desde la API
  const [planTypes, setPlanTypes] = useState(DEFAULT_PLAN_TYPES);
  const [loadingPlans, setLoadingPlans] = useState(false);
  
  // Payment notifications state
  const [paymentNotifications, setPaymentNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [showRenewDialog, setShowRenewDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    gender: '',
    password: 'test123',
    plan_type: 'premium',
    subscription_period: 'monthly',
    use_fixed_price: true,
    fixed_price: '',
    fixed_price_usd: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    grace_days: 0,
    notes: '',
    payment_status: 'paid',  // 'paid' | 'pending' — si pending, la clienta debe pagar para acceder al formulario
    program_type: ''  // 'fit_evolution' | 'asesoria_1a1' | '' (sin asignar). Filtro/contador en la tabla.
  });
  
  // Extend/Renew state
  const [extendDays, setExtendDays] = useState(7);
  const [renewEndDate, setRenewEndDate] = useState('');

  useEffect(() => {
    fetchClients();
    fetchPaymentNotifications();
    fetchVIPPlans();
  }, []);

  // Estado para precios de planes
  const [planPrices, setPlanPrices] = useState({});

  // Cargar planes disponibles para VIP desde la API
  const fetchVIPPlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await axios.get(`${API}/api/admin/vip-available-plans`, { withCredentials: false });
      if (response.data.plans && response.data.plans.length > 0) {
        const formattedPlans = response.data.plans.map(plan => ({
          value: plan.slug || plan.id || plan.plan_type,
          label: `${plan.icon || '💎'} ${plan.name}`,
          color: plan.color ? `bg-[${plan.color}]` : 'bg-purple-500',
          permissions: plan.permissions || {}
        }));
        setPlanTypes(formattedPlans);
        
        // Guardar TODOS los datos de cada plan (incluyendo precios por período)
        const prices = {};
        response.data.plans.forEach(plan => {
          const slug = plan.slug || plan.id || plan.plan_type;
          // Guardar el plan completo para acceder a todos los campos de precio
          prices[slug] = plan;
        });
        setPlanPrices(prices);
      }
    } catch (error) {
      console.error('Error fetching VIP plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/vip-clients`, { withCredentials: false });
      setClients(response.data.clients || []);
      setStats({
        total: response.data.total || 0,
        active: response.data.active || 0,
        expired: response.data.expired || 0
      });
    } catch (error) {
      console.error('Error fetching VIP clients:', error);
      toast.error('Error al cargar clientes VIP');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const response = await axios.get(`${API}/api/admin/vip-payment-notifications`, { withCredentials: false });
      setPaymentNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching payment notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleApprovePayment = async (notificationId) => {
    if (!window.confirm('¿Confirmar aprobación del pago? Esto renovará la suscripción del cliente.')) return;
    
    try {
      const response = await axios.post(
        `${API}/api/admin/vip-payment-notifications/${notificationId}/approve`,
        {},
        { withCredentials: false }
      );
      
      if (response.data.success) {
        toast.success(response.data.message || 'Pago aprobado y membresía renovada');
        fetchPaymentNotifications();
        fetchClients();
      }
    } catch (error) {
      console.error('Error approving payment:', error);
      toast.error(error.response?.data?.detail || 'Error al aprobar el pago');
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('es-CO', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateEndDate = (startDate, period) => {
    const periodInfo = SUBSCRIPTION_PERIODS.find(p => p.value === period);
    if (!periodInfo || !startDate) return '';
    
    const start = new Date(startDate);
    start.setMonth(start.getMonth() + periodInfo.months);
    return start.toISOString().split('T')[0];
  };

  // Obtener precio del plan según el período seleccionado
  const getPlanPrice = (planSlug, period) => {
    const pricing = planPrices[planSlug];
    if (!pricing) return { cop: null, usd: null };
    
    // Mapeo de períodos a nombres de campos
    const periodMap = {
      'monthly': { cop: 'price_monthly_cop', usd: 'price_monthly' },
      'bimonthly': { cop: 'price_bimonthly_cop', usd: 'price_bimonthly' },
      'quarterly': { cop: 'price_quarterly_cop', usd: 'price_quarterly' },
      'semester': { cop: 'price_semiannual_cop', usd: 'price_semiannual' },
      'yearly': { cop: 'price_yearly_cop', usd: 'price_yearly' }
    };
    
    // PRIMERO: Verificar estructura pricing.period (la más confiable)
    if (pricing.pricing?.[period]) {
      const cop = pricing.pricing[period].cop;
      const usd = pricing.pricing[period].usd;
      if (cop || usd) {
        return { cop, usd };
      }
    }
    
    // SEGUNDO: Verificar campos directos si pricing.period no existe
    const fields = periodMap[period];
    if (fields) {
      const cop = pricing[fields.cop];
      const usd = pricing[fields.usd];
      // Solo usar si el valor es positivo (no 0, null, o undefined)
      if ((cop && cop > 0) || (usd && usd > 0)) {
        return { cop: cop > 0 ? cop : null, usd: usd > 0 ? usd : null };
      }
    }
    
    // TERCERO: Fallback a precios generales del mes
    return { 
      cop: pricing.pricing?.monthly?.cop || (pricing.price_cop > 0 ? pricing.price_cop : null), 
      usd: pricing.pricing?.monthly?.usd || pricing.price || pricing.price_usd
    };
  };

  const handlePeriodChange = (period) => {
    setFormData(prev => ({
      ...prev,
      subscription_period: period,
      end_date: calculateEndDate(prev.start_date, period)
    }));
  };

  const handleStartDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      start_date: date,
      end_date: calculateEndDate(date, prev.subscription_period)
    }));
  };

  const resetForm = () => {
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      email: '',
      name: '',
      phone: '',
      gender: '',
      password: 'test123',
      plan_type: 'premium',
      subscription_period: 'monthly',
      use_fixed_price: true,
      fixed_price: '',
      fixed_price_usd: '',
      start_date: today,
      end_date: calculateEndDate(today, 'monthly'),
      grace_days: 0,
      notes: '',
      payment_status: 'paid',
      program_type: ''
    });
  };

  const handleCreateClient = async () => {
    if (!formData.email || !formData.name) {
      toast.error('Email y nombre son requeridos');
      return;
    }
    
    if (!formData.password || formData.password.length < 6) {
      toast.error('La contraseña provisional debe tener al menos 6 caracteres');
      return;
    }
    
    if (formData.use_fixed_price && !formData.fixed_price && !formData.fixed_price_usd) {
      toast.error('Ingresa al menos un precio (COP o USD)');
      return;
    }

    // 2026-02 · program_type obligatorio y exclusivo
    if (!formData.program_type || !['fit_evolution', 'asesoria_1a1'].includes(formData.program_type)) {
      toast.error('Debes elegir el tipo de programa: Fit Evolution o Asesoría 1 a 1');
      return;
    }

    try {
      const payload = {
        ...formData,
        fixed_price: formData.use_fixed_price && formData.fixed_price ? parseFloat(formData.fixed_price) : null,
        fixed_price_usd: formData.use_fixed_price && formData.fixed_price_usd ? parseFloat(formData.fixed_price_usd) : null,
        grace_days: parseInt(formData.grace_days) || 0
      };
      
      await axios.post(`${API}/api/admin/vip-clients`, payload, { withCredentials: false });
      toast.success(`Cliente VIP "${formData.name}" creado exitosamente. La contraseña provisional es: ${formData.password}`);
      setShowCreateDialog(false);
      resetForm();
      fetchClients();
    } catch (error) {
      console.error('Error creating VIP client:', error);
      toast.error(error.response?.data?.detail || 'Error al crear cliente VIP');
    }
  };

  const handleUpdateClient = async () => {
    if (!selectedClient) return;

    // 2026-02 · program_type obligatorio y exclusivo
    if (formData.program_type !== undefined && formData.program_type !== null
        && !['fit_evolution', 'asesoria_1a1'].includes(formData.program_type)) {
      toast.error('Debes elegir el tipo de programa: Fit Evolution o Asesoría 1 a 1');
      return;
    }
    
    try {
      // Send 0 to explicitly CLEAR a price field, positive number to SET it
      // Backend interprets 0 as "clear this field" and >0 as "set this value"
      const payload = {
        email: formData.email || undefined,
        name: formData.name || undefined,
        phone: formData.phone || undefined,
        gender: formData.gender || undefined,
        plan_type: formData.plan_type || undefined,
        subscription_period: formData.subscription_period || undefined,
        use_fixed_price: formData.use_fixed_price,
        fixed_price: formData.use_fixed_price && formData.fixed_price ? parseFloat(formData.fixed_price) : 0,
        fixed_price_usd: formData.use_fixed_price && formData.fixed_price_usd ? parseFloat(formData.fixed_price_usd) : 0,
        grace_days: parseInt(formData.grace_days) || 0,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        notes: formData.notes || null,
        payment_status: formData.payment_status || undefined,
        program_type: formData.program_type || null
      };
      
      await axios.put(`${API}/api/admin/vip-clients/${selectedClient.id}`, payload, { withCredentials: false });
      toast.success('Cliente VIP actualizado');
      setShowEditDialog(false);
      fetchClients();
    } catch (error) {
      console.error('Error updating VIP client:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`Error al actualizar: ${errorMsg}`);
    }
  };

  const handleDeleteClient = async (client) => {
    if (!window.confirm(`¿Eliminar cliente VIP "${client.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/api/admin/vip-clients/${client.id}`, { withCredentials: false });
      toast.success('Cliente VIP eliminado');
      fetchClients();
    } catch (error) {
      console.error('Error deleting VIP client:', error);
      toast.error('Error al eliminar cliente VIP');
    }
  };

  // Ver por qué un cliente aún tiene acceso (o no)
  const handleDiagnoseAccess = async (client) => {
    try {
      const res = await axios.get(
        `${API}/api/admin/user-access-diagnosis/${encodeURIComponent(client.email)}`,
        { withCredentials: false }
      );
      const d = res.data;
      if (!d.success) {
        toast.error(d.message || 'No se pudo diagnosticar');
        return;
      }
      const c = d.computed || {};
      const u = d.user || {};
      const v = d.vip_client || {};
      const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

      const messageLines = [
        `📋 ${client.email}`,
        ``,
        `▸ Rol: ${u.role || 'usuario normal'}`,
        `▸ Plan: ${u.subscription_plan || '—'}`,
        `▸ Fecha vencimiento (users): ${fmt(u.subscription_end_date)}`,
        `▸ VIP activo: ${v.is_active ? 'SÍ' : 'no'}`,
        `▸ VIP end_date: ${fmt(v.end_date)}`,
        ``,
        `━━━━━━━━━━━━━━━━━━━`,
        `Fecha usada: ${fmt(c.effective_end_date)} (${c.effective_source || 'ninguna'})`,
        `Días para vencer: ${c.days_until_expiry ?? 'N/A'}`,
        `Días de gracia restantes: ${c.grace_days_remaining ?? 0}`,
        ``,
        `¿Debería estar BLOQUEADA?: ${c.should_be_blocked ? '✅ SÍ' : '❌ NO'}`,
        ``,
        c.should_be_blocked
          ? 'Si tiene acceso pese a esto, refresca su sesión o presiona 🚫 para forzar bloqueo.'
          : (u.role === 'admin' ? '⚠️ Tiene rol=admin — por eso NO se bloquea.'
              : (c.effective_end_date == null
                  ? '⚠️ No tiene fecha de vencimiento registrada. Por eso no se bloquea.'
                  : '✅ Aún dentro de vigencia.'))
      ];
      alert(messageLines.join('\n'));
    } catch (error) {
      console.error('Error diagnose access:', error);
      toast.error('Error al diagnosticar acceso');
    }
  };

  // Forzar que un cliente aparezca como vencido
  const handleForceExpire = async (client) => {
    const confirmMsg = `⚠️ ¿Bloquear acceso de ${client.name} (${client.email})?\n\n` +
      `Esto pondrá su fecha de vencimiento hace 30 días y desactivará su suscripción VIP. ` +
      `La próxima vez que ella entre, verá el aviso de "Plan Vencido".\n\n` +
      `Solo úsalo si estás segura de que su plan realmente venció.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await axios.post(
        `${API}/api/admin/force-expire-user/${encodeURIComponent(client.email)}`,
        {},
        { withCredentials: false }
      );
      if (res.data?.success) {
        toast.success(`Acceso bloqueado. ${client.name} verá "Plan Vencido" al recargar.`);
        fetchClients();
      } else {
        toast.error(res.data?.message || 'No se pudo bloquear el acceso');
      }
    } catch (error) {
      console.error('Error force-expire:', error);
      toast.error(error.response?.data?.message || 'Error al bloquear acceso');
    }
  };

  // Función para resetear contraseña
  const handleResetPassword = async (client) => {
    const tempPassword = `temp${Math.random().toString(36).substring(2, 8)}`;
    
    const confirmReset = window.confirm(
      `¿Resetear contraseña de ${client.name} (${client.email})?\n\n` +
      `Nueva contraseña temporal: ${tempPassword}\n\n` +
      `El usuario deberá cambiar su contraseña al ingresar.`
    );
    
    if (!confirmReset) return;
    
    try {
      await axios.post(
        `${API}/api/admin/vip-clients/${client.id}/reset-password`,
        { 
          new_password: tempPassword,
          force_change: true 
        },
        { withCredentials: false }
      );
      
      toast.success(
        <div>
          <p><strong>Contraseña reseteada</strong></p>
          <p>Email: {client.email}</p>
          <p>Nueva contraseña: <strong>{tempPassword}</strong></p>
          <p className="text-xs mt-1">El usuario deberá cambiar su contraseña al ingresar</p>
        </div>,
        { duration: 10000 }
      );
      
      // Copiar al portapapeles
      navigator.clipboard.writeText(tempPassword).then(() => {
        toast.info('Contraseña copiada al portapapeles');
      });
      
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Error al resetear contraseña');
    }
  };

  const handleExtendGrace = async () => {
    if (!selectedClient) return;
    
    try {
      await axios.post(
        `${API}/api/admin/vip-clients/${selectedClient.id}/extend?days=${extendDays}`,
        {},
        { withCredentials: false }
      );
      toast.success(`Se agregaron ${extendDays} días de gracia`);
      setShowExtendDialog(false);
      fetchClients();
    } catch (error) {
      console.error('Error extending grace:', error);
      toast.error('Error al extender días de gracia');
    }
  };

  const handleRenewSubscription = async () => {
    if (!selectedClient || !renewEndDate) return;
    
    try {
      await axios.post(
        `${API}/api/admin/vip-clients/${selectedClient.id}/renew?new_end_date=${renewEndDate}`,
        {},
        { withCredentials: false }
      );
      toast.success('Suscripción renovada exitosamente');
      setShowRenewDialog(false);
      fetchClients();
    } catch (error) {
      console.error('Error renewing subscription:', error);
      toast.error('Error al renovar suscripción');
    }
  };

  const openEditDialog = (client) => {
    setSelectedClient(client);
    setFormData({
      email: client.email || '',
      name: client.name || '',
      phone: client.phone || '',
      gender: client.gender || '',
      plan_type: client.plan_type || 'premium',
      subscription_period: client.subscription_period || 'monthly',
      use_fixed_price: client.use_fixed_price ?? true,
      fixed_price: client.fixed_price?.toString() || '',
      fixed_price_usd: client.fixed_price_usd?.toString() || '',
      start_date: client.start_date ? new Date(client.start_date).toISOString().split('T')[0] : '',
      end_date: client.end_date ? new Date(client.end_date).toISOString().split('T')[0] : '',
      grace_days: client.grace_days || 0,
      notes: client.notes || '',
      payment_status: client.payment_status || 'paid',
      program_type: client.program_type || ''
    });
    setShowEditDialog(true);
  };

  const openExtendDialog = (client) => {
    setSelectedClient(client);
    setExtendDays(7);
    setShowExtendDialog(true);
  };

  const openRenewDialog = (client) => {
    setSelectedClient(client);
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setRenewEndDate(nextMonth.toISOString().split('T')[0]);
    setShowRenewDialog(true);
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone?.includes(searchTerm);
    
    const matchesStatus = 
      filterStatus === 'all' ||
      (filterStatus === 'active' && client.is_active && !client.is_expired) ||
      (filterStatus === 'expired' && client.is_expired) ||
      (filterStatus === 'inactive' && !client.is_active);

    // Filtro por programa: fit_evolution / asesoria_1a1 / unset
    const matchesProgram =
      filterProgram === 'all' ||
      (filterProgram === 'unset' && !client.program_type) ||
      client.program_type === filterProgram;

    return matchesSearch && matchesStatus && matchesProgram;
  });

  // Contadores por programa (independientes del filtro para siempre mostrar totales globales)
  const programCounts = {
    fit_evolution: clients.filter(c => c.program_type === 'fit_evolution').length,
    asesoria_1a1: clients.filter(c => c.program_type === 'asesoria_1a1').length,
    unset: clients.filter(c => !c.program_type).length,
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(price);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 text-white">
            <FaCrown className="text-yellow-500" /> Clientes VIP / Offline
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Gestiona clientes con precios especiales que no compran directamente desde la web
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white">
          <FaUserPlus className="mr-2" /> Nuevo Cliente VIP
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card style={{ background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(168, 85, 247, 0.1))', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-400">Total Clientes VIP</p>
                <p className="text-3xl font-bold text-white">{stats.total}</p>
              </div>
              <FaCrown className="text-4xl text-purple-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.1))', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-400">Activos</p>
                <p className="text-3xl font-bold text-white">{stats.active}</p>
              </div>
              <FaCheckCircle className="text-4xl text-green-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card style={{ background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(239, 68, 68, 0.1))', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-400">Vencidos</p>
                <p className="text-3xl font-bold text-white">{stats.expired}</p>
              </div>
              <FaTimesCircle className="text-4xl text-red-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className="cursor-pointer transition-all hover:scale-105"
          style={{ 
            background: paymentNotifications.length > 0 
              ? 'linear-gradient(135deg, rgba(249, 115, 22, 0.3), rgba(245, 158, 11, 0.2))' 
              : 'rgba(255,255,255,0.05)',
            border: paymentNotifications.length > 0 ? '2px solid rgba(249, 115, 22, 0.5)' : '1px solid rgba(255,255,255,0.1)'
          }}
          onClick={() => setActiveTab('payments')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${paymentNotifications.length > 0 ? 'text-orange-400' : 'text-gray-400'}`}>
                  Pagos Pendientes
                </p>
                <p className={`text-3xl font-bold ${paymentNotifications.length > 0 ? 'text-orange-400' : 'text-white'}`}>
                  {paymentNotifications.length}
                </p>
              </div>
              <FaBell className={`text-4xl ${paymentNotifications.length > 0 ? 'text-orange-400/70 animate-pulse' : 'text-gray-500/50'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs para Clientes y Pagos */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-96" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
          <TabsTrigger value="clients" className="flex items-center gap-2 data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-white text-gray-300">
            <FaCrown /> Clientes VIP
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-2 data-[state=active]:bg-green-500 data-[state=active]:text-white text-gray-300">
            <FaMoneyBillWave /> Liberar Membresía
            {paymentNotifications.length > 0 && (
              <Badge className="bg-orange-500 text-white ml-1">{paymentNotifications.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="space-y-4 mt-4">
      <Card style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Buscar por nombre, email o teléfono..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48 bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="expired">Vencidos</SelectItem>
                <SelectItem value="inactive">Inactivos</SelectItem>
              </SelectContent>
            </Select>
            {/* Filtro por Programa (Fit Evolution vs Asesoría 1 a 1) */}
            <Select value={filterProgram} onValueChange={setFilterProgram}>
              <SelectTrigger data-testid="filter-program-select" className="w-56 bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Programa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los programas</SelectItem>
                <SelectItem value="fit_evolution">🚀 Fit Evolution</SelectItem>
                <SelectItem value="asesoria_1a1">👑 Asesoría 1 a 1</SelectItem>
                <SelectItem value="unset">Sin asignar</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchClients} className="border-white/20 text-white hover:bg-white/10">
              <FaSync className={loading ? 'animate-spin mr-2' : 'mr-2'} /> Actualizar
            </Button>
          </div>
          {/* Contadores por programa */}
          <div className="mt-3 flex flex-wrap gap-2 text-sm">
            <span
              data-testid="counter-fit-evolution"
              className="px-3 py-1.5 rounded-full bg-emerald-600/20 border border-emerald-500/40 text-emerald-200"
            >
              🚀 Fit Evolution: <b>{programCounts.fit_evolution}</b>
            </span>
            <span
              data-testid="counter-asesoria-1a1"
              className="px-3 py-1.5 rounded-full bg-amber-600/20 border border-amber-500/40 text-amber-200"
            >
              👑 Asesoría 1 a 1: <b>{programCounts.asesoria_1a1}</b>
            </span>
            <span
              data-testid="counter-unset"
              className="px-3 py-1.5 rounded-full bg-gray-700/40 border border-gray-500/40 text-gray-300"
            >
              Sin asignar: <b>{programCounts.unset}</b>
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Clients List */}
      <Card style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <CardHeader>
          <CardTitle className="text-white">Lista de Clientes VIP ({filteredClients.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <FaSync className="animate-spin text-4xl text-purple-500 mx-auto mb-2" />
              <p className="text-gray-400">Cargando clientes...</p>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-8">
              <FaCrown className="text-4xl text-gray-500 mx-auto mb-2" />
              <p className="text-gray-400">No hay clientes VIP</p>
              <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} className="mt-4 bg-gradient-to-r from-yellow-500 to-amber-500">
                <FaPlus className="mr-2" /> Crear Primer Cliente VIP
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredClients.map((client) => (
                <div 
                  key={client.id} 
                  className="border rounded-lg p-4 transition-all hover:shadow-md"
                  style={{
                    backgroundColor: client.is_expired ? 'rgba(239, 68, 68, 0.1)' : 
                      !client.is_active ? 'rgba(255,255,255,0.03)' :
                      'rgba(234, 179, 8, 0.1)',
                    borderColor: client.is_expired ? 'rgba(239, 68, 68, 0.3)' : 
                      !client.is_active ? 'rgba(255,255,255,0.1)' :
                      'rgba(234, 179, 8, 0.3)'
                  }}
                >
                  <div className="flex items-center justify-between">
                    {/* Client Info */}
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        client.gender === 'female' ? 'bg-pink-500/20' : 'bg-blue-500/20'
                      }`}>
                        {client.gender === 'female' ? (
                          <FaVenus className="text-pink-400 text-xl" />
                        ) : (
                          <FaMars className="text-blue-400 text-xl" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg text-white">{client.name}</h3>
                          {(() => {
                            // Defensa contra plan_type mal formados (ej: "renewal_premium",
                            // "vip-renewal-gold", "Renovación Premium…"). Se limpia el prefijo,
                            // luego se busca en `planTypes`. Nunca se muestra "Renewal X".
                            const raw = String(client.plan_type || '').trim().toLowerCase();
                            const clean = raw
                              .replace(/^(vip-renewal-|vip_renewal_|renewal-|renewal_|renovación |renovacion |plan-|plan_|plan )/i, '')
                              .replace(/^básico$/i, 'basic').replace(/^basico$/i, 'basic')
                              .replace(/^competitiva$/i, 'competitivo');
                            const match = planTypes.find(p => p.value === clean);
                            const label = match?.label || (clean ? clean.charAt(0).toUpperCase() + clean.slice(1) : '—');
                            const color = match?.color || 'bg-gray-500';
                            return (
                              <Badge className={color} data-testid={`plan-badge-${client.email}`}>
                                {label}
                              </Badge>
                            );
                          })()}
                          {client.is_expired && (
                            <Badge variant="destructive">VENCIDO</Badge>
                          )}
                          {!client.is_active && !client.is_expired && (
                            <Badge variant="secondary">INACTIVO</Badge>
                          )}
                          {client.special_price_warning && (
                            <Badge className="bg-orange-500">⚠️ Precio especial en riesgo</Badge>
                          )}
                          {client.payment_status === 'pending' && !client.is_expired && (
                            <Badge className="bg-amber-500 text-black" data-testid={`payment-pending-badge-${client.id}`}>
                              ⏳ PAGO PENDIENTE
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                          <span className="flex items-center gap-1">
                            <FaEnvelope className="text-gray-500" /> {client.email}
                          </span>
                          {client.phone && (
                            <span className="flex items-center gap-1">
                              <FaPhone className="text-gray-500" /> {client.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Dates and Price */}
                    <div className="text-right">
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Periodo</p>
                          <p className="font-medium text-white">
                            {SUBSCRIPTION_PERIODS.find(p => p.value === client.subscription_period)?.label || client.subscription_period}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Vence</p>
                          <p className={`font-medium ${client.is_expired ? 'text-red-400' : 'text-white'}`}>
                            {formatDate(client.end_date)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Precio</p>
                          <p className="font-bold text-green-400">
                            {client.use_fixed_price ? (
                              <>
                                {client.fixed_price ? formatPrice(client.fixed_price) : ''}
                                {client.fixed_price && client.fixed_price_usd ? ' / ' : ''}
                                {client.fixed_price_usd ? `$${client.fixed_price_usd} USD` : ''}
                                {!client.fixed_price && !client.fixed_price_usd ? 'Sin precio' : ''}
                              </>
                            ) : 'Precio plan'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Días restantes</p>
                          <p className={`font-bold ${client.days_remaining <= 5 ? 'text-red-400' : 'text-green-400'}`}>
                            {client.days_remaining} {client.grace_days > 0 && `(+${client.grace_days} gracia)`}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-blue-400 border-blue-400/30 hover:bg-blue-500/10"
                        onClick={() => openExtendDialog(client)}
                        title="Agregar días de gracia"
                      >
                        <FaGift />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-green-400 border-green-400/30 hover:bg-green-500/10"
                        onClick={() => openRenewDialog(client)}
                        title="Renovar suscripción"
                      >
                        <FaSync />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-white border-white/20 hover:bg-white/10"
                        onClick={() => openEditDialog(client)}
                        title="Editar"
                      >
                        <FaEdit />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-yellow-400 border-yellow-400/30 hover:bg-yellow-500/10"
                        onClick={() => handleResetPassword(client)}
                        title="Resetear Contraseña"
                      >
                        <FaKey />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-400 border-red-400/30 hover:bg-red-500/10"
                        onClick={() => handleDeleteClient(client)}
                        title="Eliminar"
                      >
                        <FaTrash />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-cyan-400 border-cyan-400/30 hover:bg-cyan-500/10"
                        onClick={() => handleDiagnoseAccess(client)}
                        title="Ver por qué tiene acceso"
                        data-testid={`diagnose-access-${client.email}`}
                      >
                        <FaInfoCircle />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-orange-400 border-orange-400/30 hover:bg-orange-500/10"
                        onClick={() => handleForceExpire(client)}
                        title="Bloquear acceso ahora (forzar plan vencido)"
                        data-testid={`force-expire-${client.email}`}
                      >
                        <FaBan />
                      </Button>
                    </div>
                  </div>

                  {/* Notes */}
                  {client.notes && (
                    <div className="mt-3 pt-3 border-t border-white/10 text-sm text-gray-400">
                      <span className="font-medium text-gray-300">Notas:</span> {client.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </TabsContent>

      {/* Tab de Pagos Pendientes - Liberar Membresía */}
      <TabsContent value="payments" className="space-y-4 mt-4">
        <Card style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <FaUnlock className="text-green-500" /> Liberar Membresía - Pagos Pendientes
            </CardTitle>
            <CardDescription className="text-gray-400">
              Aprueba los pagos de clientes VIP que han notificado su pago por transferencia, Nequi u otros métodos manuales
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-end mb-4">
              <Button variant="outline" onClick={fetchPaymentNotifications} className="border-white/20 text-white hover:bg-white/10">
                <FaSync className={loadingNotifications ? 'animate-spin mr-2' : 'mr-2'} /> Actualizar
              </Button>
            </div>

            {loadingNotifications ? (
              <div className="text-center py-8">
                <FaSync className="animate-spin text-4xl text-orange-500 mx-auto mb-2" />
                <p className="text-gray-400">Cargando notificaciones...</p>
              </div>
            ) : paymentNotifications.length === 0 ? (
              <div className="text-center py-12 rounded-lg" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                <FaCheckCircle className="text-6xl text-green-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">¡Todo al día!</h3>
                <p className="text-gray-400">No hay pagos pendientes de aprobación</p>
              </div>
            ) : (
              <div className="space-y-4">
                {paymentNotifications.map((notification) => (
                  <div 
                    key={notification.id}
                    className="border-2 rounded-xl p-5 hover:shadow-lg transition-all"
                    style={{ 
                      backgroundColor: 'rgba(249, 115, 22, 0.1)', 
                      borderColor: 'rgba(249, 115, 22, 0.3)' 
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-orange-400 to-amber-500 flex items-center justify-center">
                            <FaMoneyBillWave className="text-white text-xl" />
                          </div>
                          <div>
                            <h4 className="font-bold text-lg text-white">
                              {notification.name || notification.email}
                            </h4>
                            <p className="text-sm text-gray-400">{notification.email}</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 rounded-lg p-4" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Monto</p>
                            <p className="text-xl font-bold text-green-400">
                              ${notification.amount?.toLocaleString('es-CO')} COP
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Método</p>
                            <p className="font-semibold text-white">{notification.method}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Periodo</p>
                            <p className="font-semibold text-white">{notification.period}</p>
                          </div>
                        </div>
                        
                        <p className="text-xs text-gray-500 mt-3">
                          <FaClock className="inline mr-1" />
                          Recibido: {formatDateTime(notification.created_at)}
                        </p>
                      </div>
                      
                      <div className="ml-4">
                        <Button 
                          onClick={() => handleApprovePayment(notification.id)}
                          className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-3"
                        >
                          <FaUnlock className="mr-2" /> Liberar Membresía
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
      </Tabs>
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) { setShowCreateDialog(false); setShowEditDialog(false); }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.1)' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <FaCrown className="text-yellow-500" />
              {showEditDialog ? 'Editar Cliente VIP' : 'Nuevo Cliente VIP'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {showEditDialog ? 'Modifica los datos del cliente VIP' : 'Crea una cuenta real con precio especial para tu cliente'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Email *</label>
                <Input
                  type="email"
                  placeholder="cliente@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
                <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Solo con este correo se activan los beneficios</p>
              </div>
              <div>
                <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Nombre Completo *</label>
                <Input
                  placeholder="Nombre del cliente"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Teléfono</label>
                <Input
                  placeholder="300 123 4567"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </div>
              <div>
                <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Género</label>
                <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                  <SelectTrigger style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}>
                    <SelectValue placeholder="Seleccionar género" />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: '#1a1a1a', borderColor: 'rgba(255,255,255,0.1)' }}>
                    <SelectItem value="female" style={{ color: 'white' }}>👩 Femenino</SelectItem>
                    <SelectItem value="male" style={{ color: 'white' }}>👨 Masculino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tipo de Programa (Fit Evolution vs Asesoría 1 a 1) */}
            <div>
              <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                Tipo de Programa <span style={{ color: '#f87171' }}>*</span>
                <span className="text-gray-500 font-normal"> (obligatorio y exclusivo)</span>
              </label>
              <Select
                value={formData.program_type || ''}
                onValueChange={(v) => setFormData({ ...formData, program_type: v })}
              >
                <SelectTrigger
                  data-testid="program-type-select"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderColor: !formData.program_type ? '#ef4444' : 'rgba(255,255,255,0.2)',
                    color: 'white',
                  }}
                >
                  <SelectValue placeholder="Elige el programa…" />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: '#1a1a1a', borderColor: 'rgba(255,255,255,0.1)' }}>
                  <SelectItem value="fit_evolution" style={{ color: 'white' }}>🚀 Fit Evolution</SelectItem>
                  <SelectItem value="asesoria_1a1" style={{ color: 'white' }}>👑 Asesoría 1 a 1</SelectItem>
                </SelectContent>
              </Select>
              {!formData.program_type && (
                <p className="text-xs mt-1" style={{ color: '#f87171' }} data-testid="program-type-error">
                  Debes elegir un programa — un cliente solo puede pertenecer a uno.
                </p>
              )}
            </div>

            {/* Contraseña provisional - Solo visible al crear */}
            {!showEditDialog && (
              <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(234, 179, 8, 0.15)', border: '1px solid rgba(234, 179, 8, 0.3)' }}>
                <label style={{ color: '#facc15', fontSize: '14px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>🔐 Contraseña Provisional *</label>
                <p style={{ fontSize: '12px', color: 'rgba(253, 224, 71, 0.7)', marginBottom: '8px' }}>
                  Esta contraseña permitirá al cliente iniciar sesión por primera vez.
                </p>
                <Input
                  type="text"
                  placeholder="Mínimo 6 caracteres"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </div>
            )}

            <hr className="my-4 border-white/10" />

            {/* Plan Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Tipo de Plan</label>
                <Select value={formData.plan_type} onValueChange={(v) => setFormData({ ...formData, plan_type: v })}>
                  <SelectTrigger style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: '#1a1a1a', borderColor: 'rgba(255,255,255,0.1)' }}>
                    {planTypes.map(plan => (
                      <SelectItem key={plan.value} value={plan.value} style={{ color: 'white' }}>{plan.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Periodo de Suscripción</label>
                <Select value={formData.subscription_period} onValueChange={handlePeriodChange}>
                  <SelectTrigger style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: '#1a1a1a', borderColor: 'rgba(255,255,255,0.1)' }}>
                    {SUBSCRIPTION_PERIODS.map(period => (
                      <SelectItem key={period.value} value={period.value} style={{ color: 'white' }}>{period.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Price Options */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.2)' }}>
              <label style={{ color: '#facc15', fontSize: '14px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <FaDollarSign /> Configuración de Precio
              </label>
              <div className="flex gap-4 mb-3">
                <Button
                  type="button"
                  variant={formData.use_fixed_price ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, use_fixed_price: true })}
                  className={formData.use_fixed_price ? 'bg-yellow-500 hover:bg-yellow-600 text-black' : 'border-white/20 text-white'}
                >
                  💰 Precio Fijo Especial
                </Button>
                <Button
                  type="button"
                  variant={!formData.use_fixed_price ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, use_fixed_price: false })}
                  className={!formData.use_fixed_price ? 'bg-[var(--brand-primary)]' : 'border-white/20 text-white'}
                >
                  📋 Usar Precio del Plan
                </Button>
              </div>
              {formData.use_fixed_price && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Precio Fijo (COP)</label>
                    <Input
                      type="number"
                      placeholder="Ej: 80000"
                      value={formData.fixed_price}
                      onChange={(e) => setFormData({ ...formData, fixed_price: e.target.value })}
                      style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                    />
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Precio en Pesos Colombianos</p>
                  </div>
                  <div>
                    <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Precio Fijo (USD) 💵</label>
                    <Input
                      type="number"
                      placeholder="Ej: 20"
                      value={formData.fixed_price_usd}
                      onChange={(e) => setFormData({ ...formData, fixed_price_usd: e.target.value })}
                      style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                    />
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Precio en Dólares Americanos</p>
                  </div>
                </div>
              )}
              {!formData.use_fixed_price && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(var(--brand-primary-glow), 0.1)', border: '1px solid rgba(var(--brand-primary-glow), 0.3)' }}>
                  <p className="text-sm text-gray-300 mb-3">
                    <strong style={{ color: 'var(--brand-primary)' }}>Precio del Plan:</strong> Se usará el precio configurado para el plan seleccionado
                  </p>
                  {(() => {
                    const price = getPlanPrice(formData.plan_type, formData.subscription_period);
                    const periodLabel = SUBSCRIPTION_PERIODS.find(p => p.value === formData.subscription_period)?.label || formData.subscription_period;
                    return (
                      <div className="flex gap-6">
                        <div>
                          <span className="text-gray-400 text-sm">COP: </span>
                          <span className="text-white font-bold">
                            {price.cop ? `$${price.cop.toLocaleString('es-CO')}` : 'No configurado'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-400 text-sm">USD: </span>
                          <span className="text-white font-bold">
                            {price.usd ? `$${price.usd.toLocaleString('en-US')}` : 'No configurado'}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                  {!getPlanPrice(formData.plan_type, formData.subscription_period).cop && 
                   !getPlanPrice(formData.plan_type, formData.subscription_period).usd && (
                    <p className="text-yellow-400 text-xs mt-2">
                      ⚠️ No hay precios configurados para este plan. Configúralos en "Planes de Suscripción".
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Estado de Pago */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(var(--brand-primary-glow), 0.08)', border: '1px solid rgba(var(--brand-primary-glow), 0.3)' }}>
              <label style={{ color: 'var(--brand-primary)', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                💳 Estado de Pago
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, payment_status: 'paid' })}
                  className="px-3 py-3 rounded-lg text-sm font-semibold transition-all"
                  style={{
                    backgroundColor: formData.payment_status === 'paid' ? 'rgba(34, 197, 94, 0.25)' : 'rgba(255,255,255,0.06)',
                    border: formData.payment_status === 'paid' ? '2px solid #22C55E' : '1px solid rgba(255,255,255,0.15)',
                    color: formData.payment_status === 'paid' ? '#86EFAC' : '#9ca3af'
                  }}
                  data-testid="payment-status-paid"
                >
                  ✓ Pagado
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, payment_status: 'pending' })}
                  className="px-3 py-3 rounded-lg text-sm font-semibold transition-all"
                  style={{
                    backgroundColor: formData.payment_status === 'pending' ? 'rgba(245, 158, 11, 0.25)' : 'rgba(255,255,255,0.06)',
                    border: formData.payment_status === 'pending' ? '2px solid #F59E0B' : '1px solid rgba(255,255,255,0.15)',
                    color: formData.payment_status === 'pending' ? '#FCD34D' : '#9ca3af'
                  }}
                  data-testid="payment-status-pending"
                >
                  ⏳ Pendiente de pago
                </button>
              </div>
              {formData.payment_status === 'pending' && (
                <p className="text-xs text-amber-300/80 mt-2 leading-relaxed">
                  La clienta podrá entrar, cambiar su contraseña y ver una pantalla de bienvenida con el botón &quot;Pagar&quot; por
                  {formData.fixed_price ? ` $${formData.fixed_price} COP` : ''}{formData.fixed_price_usd ? ` / $${formData.fixed_price_usd} USD` : ''}.
                  No tendrá acceso al formulario inicial hasta que complete el pago.
                </p>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                  <FaCalendar style={{ color: '#9ca3af' }} /> Fecha de Inicio
                </label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </div>
              <div>
                <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                  <FaCalendar style={{ color: '#9ca3af' }} /> Fecha de Fin
                </label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </div>
            </div>

            {/* Grace Days */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
              <label style={{ color: '#60a5fa', fontSize: '14px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <FaClock /> Días de Vigencia/Gracia
              </label>
              <Input
                type="number"
                min="0"
                value={formData.grace_days}
                onChange={(e) => setFormData({ ...formData, grace_days: e.target.value })}
                style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white', width: '128px' }}
              />
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                Días adicionales después de la fecha de fin para dar tiempo al pago
              </p>
            </div>

            {/* Notes */}
            <div>
              <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Notas (opcional)</label>
              <Textarea
                placeholder="Notas internas sobre el cliente..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); }} className="border-white/20 text-white hover:bg-white/10">
              Cancelar
            </Button>
            <Button 
              onClick={showEditDialog ? handleUpdateClient : handleCreateClient}
              className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold"
            >
              {showEditDialog ? 'Guardar Cambios' : 'Crear Cliente VIP'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Grace Dialog */}
      <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
        <DialogContent style={{ backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.1)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaGift className="text-blue-500" /> Agregar Días de Gracia
            </DialogTitle>
            <DialogDescription style={{ color: '#9ca3af' }}>
              Extiende el acceso de {selectedClient?.name} sin cambiar la fecha de vencimiento
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Días adicionales</label>
            <div className="flex gap-2 mt-2">
              {[3, 5, 7, 14, 30].map(days => (
                <Button
                  key={days}
                  variant={extendDays === days ? 'default' : 'outline'}
                  onClick={() => setExtendDays(days)}
                  size="sm"
                  className={extendDays === days ? 'bg-blue-500' : 'border-white/20 text-white'}
                >
                  {days} días
                </Button>
              ))}
            </div>
            <Input
              type="number"
              min="1"
              value={extendDays}
              onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white', width: '128px', marginTop: '8px' }}
            />
            <p style={{ fontSize: '14px', color: '#9ca3af', marginTop: '8px' }}>
              Días de gracia actuales: <strong style={{ color: 'white' }}>{selectedClient?.grace_days || 0}</strong>
              <br />
              Después de agregar: <strong style={{ color: '#4ade80' }}>{(selectedClient?.grace_days || 0) + extendDays}</strong> días
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExtendDialog(false)} className="border-white/20 text-white">Cancelar</Button>
            <Button onClick={handleExtendGrace} className="bg-blue-500 hover:bg-blue-600 text-white">
              <FaGift className="mr-2" /> Agregar {extendDays} Días
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Renew Dialog */}
      <Dialog open={showRenewDialog} onOpenChange={setShowRenewDialog}>
        <DialogContent style={{ backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.1)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaSync className="text-green-500" /> Renovar Suscripción
            </DialogTitle>
            <DialogDescription style={{ color: '#9ca3af' }}>
              Renueva la suscripción de {selectedClient?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '6px' }}>Nueva Fecha de Fin</label>
            <Input
              type="date"
              value={renewEndDate}
              onChange={(e) => setRenewEndDate(e.target.value)}
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white', marginTop: '8px' }}
            />
            <div className="flex gap-2 mt-3">
              {SUBSCRIPTION_PERIODS.map(period => (
                <Button
                  key={period.value}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    today.setMonth(today.getMonth() + period.months);
                    setRenewEndDate(today.toISOString().split('T')[0]);
                  }}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  +{period.months} mes{period.months > 1 ? 'es' : ''}
                </Button>
              ))}
            </div>
            <p style={{ fontSize: '14px', color: '#9ca3af', marginTop: '12px' }}>
              <FaExclamationTriangle className="inline text-yellow-500 mr-1" />
              Los días de gracia se reiniciarán a 0 al renovar
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenewDialog(false)} className="border-white/20 text-white">Cancelar</Button>
            <Button onClick={handleRenewSubscription} className="bg-green-500 hover:bg-green-600 text-white">
              <FaSync className="mr-2" /> Renovar Suscripción
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
